import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useMeetingHubContext } from '../../MeetingHubContext';
import { useMeetingStaffContext } from '../MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import VoteResultView from '../../VoteResultView';
import { VotingRuleType } from 'store/enums/VoteTypes';
import { MeetingSession, Motion, VotingSession } from 'store/types/Meeting';
import { ActivityType } from 'store/enums/BallotReviewTypes';
import { defaultSnackbarErrorProps } from 'util/layout';
import { meetingSessionToActivity, votingRuleTypeLabel } from 'util/meeting';

import commonStyles from 'styles/common.module.scss';

interface MotionVotingControlsProps {
  currentMotion: Motion;
  disableVoting?: boolean;
  defaultVotingRule?: VotingRuleType;
  showVotingRuleSelector?: boolean;
  requiredFields?: MotionRequiredField[];
  clearFlags?: MeetingSessionClearFlags;
  validate?: () => boolean;
  saveMotion?: () => Promise<Motion | null>;
  onDisplayMotion?: (updatedSession: MeetingSession) => void | Promise<void>;
  onClearMotion?: () => void | Promise<void>;
  onCalculateVotes?: (updatedSession: MeetingSession) => void | Promise<void>;
}

type MotionRequiredField = 'fromText' | 'toText' | 'statement' | 'reason' | 'by' | 'second';
type MeetingSessionClearFlags = {
  clearDocumentLineItem?: boolean;
  clearIpCheck?: boolean;
  clearReject?: boolean;
  clearTechnicalIssue?: boolean;
  clearComment?: boolean;
};

const requiredFieldLabel: Record<MotionRequiredField, string> = {
  fromText: 'From',
  toText: 'To',
  statement: 'Motion',
  reason: 'Reason',
  by: 'By',
  second: 'Second',
};

const fieldCheck: Record<MotionRequiredField, (m: Motion) => boolean> = {
  fromText: (m) => !!m.fromText?.trim(),
  toText: (m) => !!m.toText?.trim(),
  statement: (m) => !!m.statement?.trim(),
  reason: (m) => !!m.reason?.trim(),
  by: (m) => !!m.by?.trim(),
  second: (m) => !!m.second?.trim(),
};

const joinWithCommasAnd = (items: string[]) => {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

function applyClearFlags(session?: MeetingSession, flags?: MeetingSessionClearFlags): Partial<MeetingSession> {
  if (!session || !flags) return {};

  const { clearDocumentLineItem, clearReject, clearTechnicalIssue, clearComment, clearIpCheck } = flags;
  const patch: Partial<MeetingSession> = {};

  if (session.currentDocument && clearDocumentLineItem) {
    patch.currentDocument = {
      ...session.currentDocument,
      lineItemNumber: undefined,
      lineItemTitle: undefined,
    };
  }

  if (clearReject) {
    patch.currentReject = undefined;
  }

  if (clearTechnicalIssue) {
    patch.currentTechnicalIssue = undefined;
  }

  if (clearComment) {
    patch.currentComment = undefined;
  }

  if (clearIpCheck) {
    patch.currentIpCheckVoting = undefined;
    patch.currentIpCheckSession = undefined;
    patch.currentTechnicalIssue = undefined;
  }

  return patch;
}

const MotionVotingControls: React.FunctionComponent<MotionVotingControlsProps> = ({
  currentMotion,
  disableVoting = false,
  defaultVotingRule = VotingRuleType.SimpleMajority,
  showVotingRuleSelector = true,
  requiredFields = ['statement', 'by', 'second'],
  clearFlags,
  validate,
  saveMotion,
  onDisplayMotion,
  onClearMotion,
  onCalculateVotes,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [votingRule, setVotingRule] = useState(defaultVotingRule);

  const votingResult =
    meetingSession?.currentVotingSession?.motionId === currentMotion.id
      ? meetingSession?.currentVotingSession?.result
      : undefined;

  const validateRequiredFields = useCallback(
    (motion: Motion, required: MotionRequiredField[]) => {
      const missing = required.filter((f) => !fieldCheck[f](motion));
      if (missing.length === 0) return true;

      const missingLabels = missing.map((m) => requiredFieldLabel[m]);
      const message =
        missing.length === 1 ? `${missingLabels[0]} is required` : `${joinWithCommasAnd(missingLabels)} are required`;

      enqueueSnackbar(message, { variant: 'warning' });
      return false;
    },
    [enqueueSnackbar]
  );

  const handleDisplayMotion = useCallback(async () => {
    try {
      if (validate && !validate()) return;

      if (currentMotion.statement.trim() === '') {
        enqueueSnackbar('Motion is required', { variant: 'warning' });
        return;
      }

      const motionToUse = saveMotion ? await saveMotion() : currentMotion;
      if (!motionToUse) return;

      const updatedSession = await MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        ...applyClearFlags(meetingSession, clearFlags),
        currentMotion: motionToUse,
        currentVotingSession: undefined,
        currentApprovalLevel: undefined,
      });

      if (onDisplayMotion) await onDisplayMotion(updatedSession);

      enqueueSnackbar(`Displayed motion`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to display motion`, defaultSnackbarErrorProps);
      console.error('Error displaying motion:', error);
    }
  }, [clearFlags, currentMotion, enqueueSnackbar, meetingId, meetingSession, onDisplayMotion, saveMotion, validate]);

  const handleClearMotion = useCallback(async () => {
    try {
      await MeetingService.clearVotes(meetingId, currentMotion.id);
      await MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentMotion: undefined,
        currentVotingSession: undefined,
        currentApprovalLevel: undefined,
      });

      if (onClearMotion) await onClearMotion();

      enqueueSnackbar(`Cleared motion`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to clear motion`, defaultSnackbarErrorProps);
      console.error('Error clearing motion:', error);
    }
  }, [currentMotion, enqueueSnackbar, meetingId, meetingSession, onClearMotion]);

  const handleVote = useCallback(
    async (byInterest = false) => {
      try {
        if (validate && !validate()) return;

        if (!validateRequiredFields(currentMotion, requiredFields)) return;

        const motionToUse = saveMotion ? await saveMotion() : currentMotion;
        if (!motionToUse) return;

        const voters = attendees?.filter((a) => {
          const isVotingEligible = !byInterest || a.isVotingMember;
          const isNotSemiAffiliate = a.affiliate?.toLowerCase() !== 'semi';
          const isNotPresidingChair = a.id !== meetingSession?.presidingChairId;
          return isVotingEligible && isNotSemiAffiliate && isNotPresidingChair;
        });

        const votingSession: VotingSession = {
          motionId: motionToUse.id,
          motionStatement: motionToUse.statement,
          rule: votingRule,
          voters: voters?.map((a) => a.id) ?? [],
        };

        await MeetingService.clearVotes(meetingId, motionToUse.id);

        await MeetingService.saveMeetingSession({
          ...meetingSession,
          meetingId,
          ...applyClearFlags(meetingSession, clearFlags),
          currentMotion: motionToUse,
          currentVotingSession: votingSession,
        });

        enqueueSnackbar(`Voting session started`, { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Failed to start voting session`, defaultSnackbarErrorProps);
        console.error('Error starting voting session:', error);
      }
    },
    [
      attendees,
      clearFlags,
      currentMotion,
      enqueueSnackbar,
      meetingId,
      meetingSession,
      requiredFields,
      saveMotion,
      validate,
      validateRequiredFields,
      votingRule,
    ]
  );

  const handleIndividualVoting = useCallback(() => {
    handleVote();
  }, [handleVote]);

  const handleVoteByInterest = useCallback(() => {
    handleVote(true);
  }, [handleVote]);

  const handleCalculateResult = useCallback(async () => {
    try {
      const updatedSession = await MeetingService.evaluateVotingResult(meetingId);

      if (!updatedSession) {
        enqueueSnackbar(`Voting not in session`, { variant: 'warning' });
        return;
      }

      logActivity?.({
        ...meetingSessionToActivity(updatedSession),
        type: ActivityType.Motion,
        motion: updatedSession.currentMotion,
        votingSession: updatedSession.currentVotingSession,
      });

      if (onCalculateVotes) await onCalculateVotes(updatedSession);

      enqueueSnackbar(`Calculated votes`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to calculate votes`, defaultSnackbarErrorProps);
      console.error('Error calculating votes:', error);
    }
  }, [enqueueSnackbar, logActivity, meetingId, onCalculateVotes]);

  useEffect(() => {
    setVotingRule(defaultVotingRule);
  }, [defaultVotingRule]);

  return (
    <GridContainer>
      <GridItem className={commonStyles.buttonGroup}>
        <Button variant="contained" color="secondary" onClick={handleDisplayMotion}>
          Display Motion
        </Button>
        <Button variant="contained" color="error" onClick={handleClearMotion}>
          Clear Motion
        </Button>
      </GridItem>
      {!disableVoting && (
        <GridItem>
          <FormControl>
            {showVotingRuleSelector && <FormLabel>Voting Rule</FormLabel>}
            <Box className={commonStyles.buttonGroup} sx={{ maxHeight: 40 }}>
              {showVotingRuleSelector && (
                <RadioGroup
                  row
                  name="voting-rule-radio-group"
                  defaultValue={VotingRuleType.SimpleMajority}
                  onChange={(event) => setVotingRule(event.target.value as VotingRuleType)}
                >
                  <FormControlLabel
                    value={VotingRuleType.SimpleMajority}
                    control={<Radio />}
                    label={votingRuleTypeLabel[VotingRuleType.SimpleMajority]}
                  />
                  <FormControlLabel
                    value={VotingRuleType.TwoThirdsMajority}
                    control={<Radio />}
                    label={votingRuleTypeLabel[VotingRuleType.TwoThirdsMajority]}
                  />
                </RadioGroup>
              )}
              <ButtonGroup variant="contained" sx={{ boxShadow: 0 }}>
                <Button variant="contained" color="primary" onClick={handleVoteByInterest}>
                  Vote By Interest
                </Button>
                <Button variant="contained" color="primary" onClick={handleIndividualVoting}>
                  Individual Voting
                </Button>
              </ButtonGroup>
              <Button variant="contained" color="secondary" onClick={handleCalculateResult}>
                Calculate Votes
              </Button>
            </Box>
          </FormControl>
        </GridItem>
      )}
      {votingResult && (
        <>
          <GridItem md={2}>
            <Typography>
              <strong>Result:</strong>
            </Typography>
          </GridItem>
          <GridItem md={10}>
            <VoteResultView votingResult={votingResult} />
          </GridItem>
        </>
      )}
    </GridContainer>
  );
};

export default MotionVotingControls;
